import * as React from 'react';

import {
  createStyles,
  Grid,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  WithStyles,
  withStyles
} from '@material-ui/core';
import { ResponsiveBar } from '@nivo/bar';

import numeral from 'numeral';

import getChartTheme from './getChartTheme';
import InfoBubble from './InfoBubble';

const styles = (theme: Theme) =>
  createStyles({
    barChart: {
      position: 'relative',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(6),
      height: 220
    },
    title: {
      fontSize: '1em'
    },
    toolTip: {
      zIndex: 1000
    }
  });

interface IBarChartProps {
  variant?: 'percent' | 'number' | 'plain-percent';
  format?: 'dollar';
}

export interface BarChartDatum {
  id: string;
  lieuucation: number;
  cbsa: number;
}

const BarChart: React.FC<{
  data: BarChartDatum[];
  sumLieuucation: number;
} & IBarChartProps> = ({ data, variant, format, sumLieuucation }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const chartTheme = getChartTheme(isSmall, theme);

  const sumCbsa = data.reduce((prev, curr) => curr.cbsa + prev, 0);

  const keysSet: Set<string> = new Set<string>();

  data.forEach((e) => keysSet.add(e.id));

  const dataMapped = (variant === 'number'
    ? data.map((e) => ({
        ...e,
        Micromarket: e.lieuucation,
        City: e.cbsa
      }))
    : variant === 'plain-percent'
    ? data.map((e) => ({
        ...e,
        Micromarket: e.lieuucation * 100,
        City: e.cbsa * 100
      }))
    : data.map((e) => ({
        ...e,
        Micromarket: (e.lieuucation * 100) / sumLieuucation,
        City: (e.cbsa * 100) / sumCbsa
      }))
  ).filter((e) => e.Micromarket > 0 && e.City > 0);

  return (
    <ResponsiveBar
      borderColor={{ from: 'color', modifiers: [['darker', 0.5]] }}
      data={dataMapped}
      groupMode="grouped"
      enableLabel={true}
      label={(data) =>
        variant === 'number'
          ? format === 'dollar'
            ? numeral(Number(data.value)).format('$0')
            : numeral(Number(data.value)).format('0.0')
          : numeral(Number(data.value) / 100).format('0%')
      }
      enableGridY={false}
      enableGridX={false}
      keys={['Micromarket', 'City']}
      axisLeft={null}
      borderRadius={4}
      borderWidth={1}
      colors={{ scheme: 'pastel1' }}
      margin={{ top: 25, right: 20, bottom: 30, left: 20 }}
      isInteractive={false}
      // tooltip={null}
      // tooltip={(data) => <div>{numeral(data.value / 100).format('0%')}</div>}
      animate={true}
      legends={[
        {
          dataFrom: 'keys',
          anchor: 'top-right',
          itemTextColor: chartTheme.axis.legend.text.fill,
          direction: 'row',
          justify: false,
          translateX: 15,
          translateY: -25,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      theme={chartTheme}
    />
  );
};

type IProps = WithStyles<typeof styles> & {
  title: string;
  data: BarChartDatum[];
  description: React.ReactNode;
} & IBarChartProps;

const BarChartWithTitle: React.FC<IProps> = ({
  classes,
  title,
  description,
  data,
  variant = 'percent',
  format
}) => {
  const sumLieuucation = data.reduce(
    (prev, curr) => curr.lieuucation + prev,
    0
  );

  if (sumLieuucation <= 0) {
    return <></>;
  }

  return (
    <Grid item xs={12} md={6}>
      <Paper className={classes.barChart}>
        <Typography className={classes.title} gutterBottom variant="overline">
          {title.toUpperCase()}
        </Typography>

        <InfoBubble title={title} body={description} />

        <BarChart
          variant={variant}
          format={format}
          data={data}
          sumLieuucation={sumLieuucation}
        />
      </Paper>
    </Grid>
  );
};

export default withStyles(styles)(BarChartWithTitle);
